/**
 * Site Intelligence Staging Queue Types
 */

export type StagingStatus = 'pending' | 'approved' | 'processing' | 'completed' | 'failed';

export interface StagingQueueItem {
  id: string;

  // File info
  filename: string;
  original_filename: string;
  s3_key: string;
  file_size: number;
  mime_type: string;
  page_count: number | null;

  // Metadata (filled during review)
  document_type: string | null;
  protocol_id: string | null;
  scope: 'global' | 'network' | 'site' | 'protocol';
  title: string | null;

  // AI suggestions
  suggested_protocol_id: string | null;
  suggestion_confidence: number | null; // 0.00 to 1.00
  suggestion_reason: string | null;
  first_page_text: string | null;

  // Context
  site_id: string;
  network_id: string | null;
  uploaded_by: string;
  upload_batch_id: string | null;

  // Status
  status: StagingStatus;
  error_message: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  expires_at: string;

  // Joined data (from API)
  protocol?: {
    id: string;
    protocol_number: string;
    sponsor_name: string | null;
    study_nickname: string | null;
  } | null;
  suggested_protocol?: {
    id: string;
    protocol_number: string;
    sponsor_name: string | null;
    study_nickname: string | null;
  } | null;
  uploader?: {
    id: string;
    full_name: string | null;
    email: string;
  } | null;

  // Client-side state
  presigned_url?: string;
}

export interface StagingUploadResponse {
  items: StagingQueueItem[];
  batch_id: string;
}

export interface StagingUpdateRequest {
  document_type?: string | null;
  protocol_id?: string | null;
  scope?: 'global' | 'network' | 'site' | 'protocol';
  title?: string | null;
}

export interface StagingApproveRequest {
  item_ids: string[];
}

export interface StagingApproveResponse {
  approved: number;
  failed: number;
  errors: Array<{
    item_id: string;
    error: string;
  }>;
}

// Confidence level thresholds for UI display
export const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.8,    // Green - likely correct
  MEDIUM: 0.5,  // Yellow - needs review
  LOW: 0,       // Red - probably wrong or no match
} as const;

export function getConfidenceLevel(confidence: number | null): 'high' | 'medium' | 'low' | 'none' {
  if (confidence === null) return 'none';
  if (confidence >= CONFIDENCE_THRESHOLDS.HIGH) return 'high';
  if (confidence >= CONFIDENCE_THRESHOLDS.MEDIUM) return 'medium';
  return 'low';
}

export function getConfidenceColor(confidence: number | null): string {
  const level = getConfidenceLevel(confidence);
  switch (level) {
    case 'high': return 'text-green-600 dark:text-green-400';
    case 'medium': return 'text-yellow-600 dark:text-yellow-400';
    case 'low': return 'text-red-600 dark:text-red-400';
    case 'none': return 'text-gray-400 dark:text-gray-500';
  }
}

export function getConfidenceBgColor(confidence: number | null): string {
  const level = getConfidenceLevel(confidence);
  switch (level) {
    case 'high': return 'bg-green-50 dark:bg-green-900/20';
    case 'medium': return 'bg-yellow-50 dark:bg-yellow-900/20';
    case 'low': return 'bg-red-50 dark:bg-red-900/20';
    case 'none': return 'bg-gray-50 dark:bg-gray-800';
  }
}
